/**
 * Bid increment tables.
 *
 * Price-banded: the step between asks grows with the money on the table. These
 * are per-tenant and per-lot overridable, and the clerk can override live via
 * SET_ASK (spec 8.4) - the table only drives *normal* advancement, which rides
 * inside BID_ACCEPTED.nextAsk and never emits its own event.
 */

export interface IncrementBand {
  /** Applies while the current high bid is below this threshold. The final
   *  band uses Infinity. */
  readonly below: number;
  readonly step: number;
}

export interface IncrementTable {
  readonly id: string;
  readonly bands: readonly IncrementBand[];
}

/** Default schedule from the deep-dive: $500 steps under $10k, $1k to $50k,
 *  $2.5k to $100k, $5k above. */
export const DEFAULT_INCREMENT_TABLE: IncrementTable = {
  id: 'default-v1',
  bands: [
    { below: 10_000, step: 500 },
    { below: 50_000, step: 1_000 },
    { below: 100_000, step: 2_500 },
    { below: Number.POSITIVE_INFINITY, step: 5_000 },
  ],
};


/** Regional consignment-sale schedule, calibrated from a real 118-lot
 *  BidWrangler horse sale (hammers $1.1k-$8.5k, plus a tack session selling
 *  at tens of dollars). See docs/context/bidwrangler-api-notes.md section 3. */
export const REGIONAL_INCREMENT_TABLE: IncrementTable = {
  id: 'regional-v1',
  bands: [
    { below: 10, step: 1 },
    { below: 25, step: 2 },
    { below: 100, step: 5 },
    { below: 500, step: 10 },
    { below: 1_000, step: 25 },
    { below: 5_000, step: 50 },
    { below: 10_000, step: 100 },
    { below: 25_000, step: 250 },
    { below: 50_000, step: 500 },
    { below: 100_000, step: 1_000 },
    { below: 250_000, step: 2_500 },
    { below: 500_000, step: 5_000 },
    { below: Number.POSITIVE_INFINITY, step: 10_000 },
  ],
};

const TABLES = new Map<string, IncrementTable>([
  [DEFAULT_INCREMENT_TABLE.id, DEFAULT_INCREMENT_TABLE],
  [REGIONAL_INCREMENT_TABLE.id, REGIONAL_INCREMENT_TABLE],
]);

export function getIncrementTable(id: string): IncrementTable {
  const table = TABLES.get(id);
  if (!table) throw new Error(`Unknown increment table: ${id}`);
  return table;
}

export function registerIncrementTable(table: IncrementTable): void {
  TABLES.set(table.id, table);
}

/** The step that applies at a given price. */
export function stepFor(amount: number, table: IncrementTable): number {
  for (const band of table.bands) {
    if (amount < band.below) return band.step;
  }
  // Unreachable with a well-formed table (final band is Infinity), but the
  // compiler cannot know that and a silent undefined here would be a
  // correctness bug rather than a crash.
  const last = table.bands[table.bands.length - 1];
  if (!last) throw new Error(`Increment table ${table.id} has no bands`);
  return last.step;
}

/**
 * The ask that follows a bid of `highBid`.
 *
 * Rounded up to a multiple of the step so the ladder stays clean after a clerk
 * override lands on an off-increment number - e.g. the auctioneer takes a
 * half-cut at $87,500 and the next ask should be $90,000, not $92,500.
 */
export function nextAsk(highBid: number, table: IncrementTable): number {
  return nextAskByStep(highBid, stepFor(highBid, table));
}

/**
 * ADR-70 cut 6d: the ask that follows a bid of `highBid` when the clerk chose
 * the step for the lot. Same rounding as `nextAsk`: up to a multiple of the
 * step, so $1,050 bidding by $100 asks $1,100.
 */
export function nextAskByStep(highBid: number, step: number): number {
  return Math.floor(highBid / step) * step + step;
}

// ---------------------------------------------------------------------------
// ADR-69 Phase 2: the table a SALE configured, carried to the ring.
//
// sales.increment_table (and the company's saleDefaults.incrementTable) is
// opaque jsonb that the office writes as either `{ id }` (one of the shipped
// tables) or `{ id?, bands: [{ below, step }] }` with `below: null` on the
// last band (the admin's asIncrementTable). Until Phase 2 nothing read it on
// the engine side: every ring ran default-v1. The control plane now parses it
// here, serializes it into the ring's environment (JSON cannot carry
// Infinity, hence `below: null`), and the ring registers it before the engine
// starts so `incrementTableId` resolves. The reducer still looks tables up by
// id and nothing else changes.
// ---------------------------------------------------------------------------

export type IncrementTableConfigReason = 'MALFORMED' | 'UNKNOWN_TABLE' | 'EMPTY_BANDS' | 'BAD_BAND' | 'BANDS_NOT_ASCENDING';

export type IncrementTableConfigResult =
  | { readonly ok: true; readonly table: IncrementTable }
  | { readonly ok: false; readonly reason: IncrementTableConfigReason };

/** The wire shape: `below: null` = the open-ended last band. */
export interface SerializedIncrementTable {
  readonly id: string;
  readonly bands: readonly { readonly below: number | null; readonly step: number }[];
}

/** FNV-1a over the band list, so a custom table's id is stable across the
 *  control plane and the ring without anyone choosing a name. */
function bandsHash(bands: readonly IncrementBand[]): string {
  let h = 0x811c9dc5;
  const text = bands.map((b) => (Number.isFinite(b.below) ? String(b.below) : 'inf') + ':' + String(b.step)).join(',');
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * null / undefined = the engine default. `{ id }` alone = a registered table
 * (UNKNOWN_TABLE when it is not one). Bands = a custom table: steps positive,
 * thresholds positive and strictly ascending, `below` null or absent only on
 * the last band (it becomes Infinity; a finite last threshold is widened to
 * Infinity, which is what stepFor already did by falling through). The id is
 * the configured one or `custom-<hash>`.
 */
export function incrementTableFromConfig(raw: unknown): IncrementTableConfigResult {
  if (raw === null || raw === undefined) return { ok: true, table: DEFAULT_INCREMENT_TABLE };
  if (!isRecord(raw)) return { ok: false, reason: 'MALFORMED' };
  const id = raw['id'];
  if (id !== undefined && (typeof id !== 'string' || id.trim() === '')) return { ok: false, reason: 'MALFORMED' };
  const rawBands = raw['bands'];
  if (rawBands === undefined || rawBands === null) {
    if (typeof id !== 'string') return { ok: false, reason: 'MALFORMED' };
    const table = TABLES.get(id);
    return table ? { ok: true, table } : { ok: false, reason: 'UNKNOWN_TABLE' };
  }
  if (!Array.isArray(rawBands)) return { ok: false, reason: 'MALFORMED' };
  if (rawBands.length === 0) return { ok: false, reason: 'EMPTY_BANDS' };
  const bands: IncrementBand[] = [];
  for (let i = 0; i < rawBands.length; i++) {
    const b: unknown = rawBands[i];
    if (!isRecord(b)) return { ok: false, reason: 'BAD_BAND' };
    const step = b['step'];
    if (typeof step !== 'number' || !Number.isFinite(step) || step <= 0) return { ok: false, reason: 'BAD_BAND' };
    const below = b['below'];
    const last = i === rawBands.length - 1;
    const prev = bands[bands.length - 1];
    if (below === null || below === undefined) {
      if (!last) return { ok: false, reason: 'BAD_BAND' };
      bands.push({ below: Number.POSITIVE_INFINITY, step });
      continue;
    }
    if (typeof below !== 'number' || Number.isNaN(below) || below <= 0) return { ok: false, reason: 'BAD_BAND' };
    if (prev && !(below > prev.below)) return { ok: false, reason: 'BANDS_NOT_ASCENDING' };
    bands.push({ below: last ? Number.POSITIVE_INFINITY : below, step });
  }
  return { ok: true, table: { id: typeof id === 'string' ? id : 'custom-' + bandsHash(bands), bands } };
}

/** Infinity becomes null so the table survives JSON (the ring's environment). */
export function serializeIncrementTable(table: IncrementTable): SerializedIncrementTable {
  return { id: table.id, bands: table.bands.map((b) => ({ below: Number.isFinite(b.below) ? b.below : null, step: b.step })) };
}
